import { FormState, FormStateErrors } from '@constants/commonApp/types';
import useRecommenderStore from '@store/RecommenderStore';
import useRecommenderProfileStore, { SectionName } from '@store/RecommenderProfileStore';
import { formatErrorText } from '@utils/date/dateUtils';
import { ChangeEvent, useEffect } from 'react';

interface UseFormSectionProps<T> {
  initialState: T | null;
  formState: FormState<T>;
  initializeValues: () => T;
  sectionName: SectionName;
  additionalValidations?: (newFormState: FormState<T>) => FormStateErrors<T>;
}

export const useFormSection = <T,>({
  initialState,
  formState,
  initializeValues,
  sectionName,
  additionalValidations,
}: UseFormSectionProps<T>) => {
  const { recommenderProfile } = useRecommenderStore((store) => store);

  const { setInitialData, setSectionFormState } = useRecommenderProfileStore((store) => store);

  const initializeData = () => {
    setInitialData(sectionName, initializeValues());
  };

  useEffect(() => {
    initializeData();
  }, [recommenderProfile]);

  const updateFormValues = (fieldUpdates: Partial<T>, errors?: Record<string, string>) => {
    const newFormState = {
      ...formState,
      data: {
        ...formState.data,
        ...fieldUpdates,
      },
      errors: {
        ...formState.errors,
        ...errors,
      },
    };

    const fields = Object.keys(newFormState.data) as Array<keyof T>;
    newFormState.hasChanged = fields.some((field) => {
      if (!initialState) {
        return false;
      }

      return newFormState.data[field] !== initialState[field];
    });

    if (additionalValidations) {
      const newErrors = additionalValidations?.(newFormState);

      newFormState.errors = {
        ...formState.errors,
        ...newErrors,
      };
    }

    newFormState.isFormValid = Object.values(newFormState.errors as Record<string, string>).every(
      (error) => error?.length === 0 || error === undefined,
    );

    setSectionFormState(sectionName, newFormState);
  };

  const handleDateChange = (fieldName: keyof T) => (value: Date | null) => {
    if (value) {
      updateFormValues({
        [fieldName]: `${value.getMonth() + 1}/${value.getFullYear()}`.padStart(7, '0'),
      } as Partial<T>);
    } else {
      updateFormValues({ [fieldName]: undefined } as Partial<T>);
    }
  };

  const handleRadioChange = (fieldName: string) => (event: ChangeEvent<HTMLInputElement>) => {
    updateFormValues({ [fieldName]: event.target.value } as Partial<T>);
  };

  const handleDateError = (fieldName: string, error: string | undefined) => {
    updateFormValues(formState.data, formatErrorText(fieldName, error, formState.errors as Record<string, string>));
  };

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateFormValues({ [event.target.name]: event.target.value } as Partial<T>);
  };

  const handleFileChange = (field: string) => async (file: string | undefined) => {
    updateFormValues({ [field]: file } as Partial<T>);
  };

  return {
    updateFormValues,
    handleDateChange,
    handleRadioChange,
    handleDateError,
    handleTextFieldChange,
    handleFileChange,
  };
};
